import { useState, useEffect, useCallback } from 'react';
import { API } from '@/utils/API';
import type { AssignableRole } from '@/hooks/submissions/useAssignableRoles';

export interface SubmissionUser {
  id: string;
  user_id: string;
  name: string;
  email: string;
  first_name: string;
  last_name: string;
  role_key: string;
  role_name: string;
  phone_number: string | null;
  role_level: 'study' | 'site' | 'submission' | null;
  site_id: string | null;
  site_name: string | null;
  submission_id: string | null;
  submission_title: string | null;
  submission_display_id: string | null;
  /** Roles this row can be changed to — derived from its own resource level */
  assignable_roles: AssignableRole[];
}

export interface SubmissionInvitation {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  name: string;
  role_key: string;
  role_name: string;
  state: string;
  created_at: string;
  /** Level of the resource the invitation grants a role on */
  role_level: 'study' | 'site' | 'submission' | null;
  /** Set for submission-level invitations listed on the Site Users tab */
  submission_id: string | null;
  submission_display_id: string | null;
}

interface UseSubmissionUsersReturn {
  users: SubmissionUser[];
  invitations: SubmissionInvitation[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetches users and invitations for a resource.
 * @param resourcePath - Full path prefix, e.g. /submissions/123 or /studies/456
 */
export const useSubmissionUsers = (
  resourcePath: string
): UseSubmissionUsersReturn => {
  const [users, setUsers] = useState<SubmissionUser[]>([]);
  const [invitations, setInvitations] = useState<SubmissionInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    let usersError = null;

    try {
      // Fetch users and invitations from separate endpoints
      // Use Promise.allSettled to handle partial failures gracefully
      const [usersResult, invitationsResult] = await Promise.allSettled([
        API.authenticated.fetch(`${resourcePath}/users`),
        API.authenticated.fetch(`${resourcePath}/invitations`),
      ]);

      // Handle users response
      if (usersResult.status === 'fulfilled' && usersResult.value.ok) {
        const usersData = await usersResult.value.json();
        setUsers(Array.isArray(usersData) ? usersData : []);
      } else {
        setUsers([]);
        usersError = 'Failed to fetch submission users';
      }

      // Handle invitations response
      if (
        invitationsResult.status === 'fulfilled' &&
        invitationsResult.value.ok
      ) {
        const invitationsData = await invitationsResult.value.json();
        setInvitations(Array.isArray(invitationsData) ? invitationsData : []);
      } else {
        setInvitations([]);
        // Silently fail for invitations if users succeed
      }

      // Set error if users failed (users are more critical)
      if (usersError) {
        setError(usersError);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      setUsers([]);
      setInvitations([]);
    } finally {
      setIsLoading(false);
    }
  }, [resourcePath]);

  useEffect(() => {
    if (resourcePath) {
      fetchUsers();
    }
  }, [resourcePath, fetchUsers]);

  return {
    users,
    invitations,
    isLoading,
    error,
    refetch: fetchUsers,
  };
};
